import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Users, Dumbbell, Coffee, BookOpen, Trophy, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const ICONS = { users: Users, dumbbell: Dumbbell, coffee: Coffee, book: BookOpen, trophy: Trophy };
const CHIPS = [
  { label: "30 min", window_min: 30 },
  { label: "1 hour", window_min: 60 },
  { label: "2 hours", window_min: 120 },
  { label: "Tonight", query: "tonight" },
];

// Shared companion UI — used both as the Today card and inside the "Ask jeeo" modal.
export function CompanionBody({ onClose, autoFocus = false }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const ask = async (body) => {
    setLoading(true);
    try {
      const r = await api.post("/companion/ask", body);
      setResult(r.data);
    } catch {
      setResult({ summary: "Couldn't reach jeeo just now — try again in a moment.", suggestions: [] });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!query.trim() || loading) return;
    ask({ query: query.trim() });
  };

  const go = (cta) => { navigate(cta); onClose && onClose(); };

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0">
          <Sparkles size={17} strokeWidth={1.8} />
        </span>
        <div className="min-w-0">
          <div className="font-semibold text-sm leading-tight">What should I do?</div>
          <div className="text-xs text-muted-foreground">Ask jeeo how to spend your free time</div>
        </div>
      </div>

      <form onSubmit={onSubmit} className="flex items-center gap-2">
        <input
          data-testid="companion-input"
          autoFocus={autoFocus}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="e.g. I have 2 hours free before my next class"
          className="flex-1 min-w-0 bg-secondary/60 border border-border rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand transition-colors"
        />
        <button
          type="submit"
          data-testid="companion-ask-btn"
          disabled={loading || !query.trim()}
          className="shrink-0 w-10 h-10 rounded-lg bg-brand text-white flex items-center justify-center disabled:opacity-40 hover:opacity-90 transition-opacity"
        >
          {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} strokeWidth={2} />}
        </button>
      </form>

      <div className="flex flex-wrap gap-1.5 mt-2.5">
        {CHIPS.map((c) => (
          <button
            key={c.label}
            data-testid={`companion-chip-${c.label.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => ask(c.query ? { query: c.query } : { window_min: c.window_min })}
            disabled={loading}
            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-border text-muted-foreground hover:border-brand hover:text-brand disabled:opacity-40 transition-colors"
          >
            {c.label}
          </button>
        ))}
      </div>

      <AnimatePresence>
        {result && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <p data-testid="companion-summary" className="text-sm font-medium text-foreground mt-3.5 leading-relaxed">
              {result.summary}
            </p>
            <div className="grid grid-cols-1 gap-1.5 mt-2.5">
              {(result.suggestions || []).map((s, i) => {
                const Icon = ICONS[s.icon] || Sparkles;
                return (
                  <button
                    key={i}
                    data-testid={`companion-suggestion-${i}`}
                    onClick={() => go(s.cta)}
                    className="w-full text-left flex items-center gap-3 rounded-xl border border-border px-3 py-2.5 hover:border-brand hover:bg-brand/[0.03] transition-colors group"
                  >
                    <span className="w-8 h-8 rounded-lg bg-secondary text-brand flex items-center justify-center shrink-0">
                      <Icon size={16} strokeWidth={1.8} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">{s.title}</div>
                      {s.subtitle && <div className="text-xs text-muted-foreground truncate">{s.subtitle}</div>}
                    </div>
                    <ChevronRight size={15} strokeWidth={2} className="shrink-0 text-muted-foreground group-hover:translate-x-0.5 group-hover:text-brand transition-all" />
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// Today-page card wrapper.
export default function CampusCompanion() {
  return (
    <div data-testid="campus-companion" className="mb-5 bg-white border border-border rounded-2xl p-4">
      <CompanionBody />
    </div>
  );
}
